/**
 * @fileoverview PHARMAKON — Alertes équipe : le LECTEUR de flux SSE (côté web).
 * @module services/alerteEquipe.sse
 *
 * PLAN_mobile_pharma_owner_manager §3.4 / §4.3.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CE MODULE N'EST PAS LE CHEMIN DE CORRECTION — c'est une OPTIMISATION DE
 * LATENCE. Le chemin de correction est le SONDAGE, qui reste armé flux ouvert
 * (`poll_stream_ms`). Tout ce que le flux perd — coupure réseau, redémarrage du
 * serveur, déploiement multi-réplique — est rattrapé au sondage suivant. Ne
 * jamais réécrire ce fichier pour qu'une alerte DÉPENDE de lui.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * POURQUOI `fetch` ET PAS `EventSource`. `EventSource` ne sait pas poser
 * d'en-tête `Authorization` ; le seul moyen de l'authentifier serait de mettre
 * le jeton dans l'URL — où il finirait dans les journaux d'accès nginx et dans
 * l'historique du navigateur. Le serveur REFUSE explicitement le jeton en
 * paramètre d'URL (`alerte-equipe.controller.js`). On lit donc le corps en
 * flux avec `fetch` + `ReadableStream`, et on analyse les trames nous-mêmes.
 *
 * REPRISE PAR IDENTIFIANT. Chaque trame porte un `id:` ; on le mémorise et on
 * le renvoie en `Last-Event-ID` à la reconnexion. Le serveur rejoue ce qui
 * manque, ou ordonne un `resync` si son tampon a débordé. Perdre une trame ne
 * perd donc jamais une alerte.
 */
import { API_BASE_URL } from "../config/app.config.js";

/**
 * Analyseur de trames SSE — PUR, donc testable sans réseau ni DOM.
 *
 * Consomme un tampon de texte, rend les événements COMPLETS qu'il contient
 * (une trame se termine par une ligne vide) et le RESTE non terminé, qui devra
 * être préfixé au prochain morceau reçu.
 *
 * Les lignes commençant par « : » sont des commentaires (le battement `: ping`
 * du serveur) : elles ne produisent aucun événement, mais leur ARRIVÉE prouve
 * que la connexion est vivante — l'appelant s'en sert comme signal de santé.
 *
 * @param {string} tampon
 * @returns {{ evenements: Array<{id: string|null, event: string, data: any, commentaire: boolean}>, reste: string }}
 */
export function analyserTrames(tampon) {
  const evenements = [];
  // Les serveurs peuvent envoyer \r\n ; on normalise avant de découper.
  const normalise = String(tampon ?? "").replace(/\r\n/g, "\n");
  const morceaux = normalise.split("\n\n");
  // Le dernier morceau n'est terminé par aucune ligne vide : il est incomplet.
  const reste = morceaux.pop() ?? "";

  for (const bloc of morceaux) {
    if (bloc.trim() === "") continue;

    let id = null;
    let event = "message";
    const datas = [];
    let commentaire = false;

    for (const ligne of bloc.split("\n")) {
      if (ligne === "") continue;
      if (ligne.startsWith(":")) {
        commentaire = true;
        continue;
      }
      const sep = ligne.indexOf(":");
      const champ = sep === -1 ? ligne : ligne.slice(0, sep);
      // Un unique espace après le « : » fait partie de la syntaxe, pas de la valeur.
      let valeur = sep === -1 ? "" : ligne.slice(sep + 1);
      if (valeur.startsWith(" ")) valeur = valeur.slice(1);

      if (champ === "id") id = valeur;
      else if (champ === "event") event = valeur;
      else if (champ === "data") datas.push(valeur);
      // `retry:` est honoré par le serveur pour EventSource ; notre boucle de
      // reconnexion a sa propre cadence, on l'ignore sans bruit.
    }

    if (datas.length === 0) {
      // Un bloc qui ne portait qu'un commentaire (battement) : on le remonte
      // pour que l'appelant sache que la connexion respire.
      if (commentaire) evenements.push({ id, event: "ping", data: null, commentaire: true });
      continue;
    }

    const brut = datas.join("\n");
    let data = brut;
    try {
      data = JSON.parse(brut);
    } catch {
      /* le serveur envoie du JSON ; un jour où il n'en enverrait pas, on rend le texte */
    }
    evenements.push({ id, event, data, commentaire });
  }

  return { evenements, reste };
}

/**
 * Ouvre le flux et pompe les événements jusqu'à l'abandon.
 *
 * Ne relance RIEN tout seul : la reconnexion appartient à l'appelant (le
 * magasin), qui sait s'il faut se reconnecter, avec quelle cadence, et qui tient
 * de toute façon le sondage de correction.
 *
 * @param {object} p
 * @param {AbortSignal} p.signal
 * @param {string|null} [p.lastEventId] identifiant du dernier événement reçu
 * @param {(e: {id: string|null, event: string, data: any, commentaire: boolean}) => void} p.onEvenement
 * @returns {Promise<{ raison: string }>} résolue à la FERMETURE du flux
 */
export async function ouvrirFlux({ signal, lastEventId = null, onEvenement }) {
  const jeton = localStorage.getItem("pharmakon_token");
  if (!jeton) return { raison: "SANS_JETON" };

  const entetes = {
    Accept: "text/event-stream",
    Authorization: `Bearer ${jeton}`,
  };
  if (lastEventId) entetes["Last-Event-ID"] = lastEventId;

  let reponse;
  try {
    reponse = await fetch(`${API_BASE_URL}/alertes-equipe/stream`, {
      method: "GET",
      headers: entetes,
      signal,
      // Le flux est authentifié par en-tête, pas par cookie.
      credentials: "omit",
      cache: "no-store",
    });
  } catch {
    return { raison: "RESEAU" };
  }

  // 503 = flux désactivé côté serveur, ou trop de connexions : on retombe sur
  // le sondage sans bruit, c'est le comportement prévu par le plan.
  if (!reponse.ok || !reponse.body) {
    return { raison: reponse.status === 503 ? "INDISPONIBLE" : `HTTP_${reponse.status}` };
  }

  const lecteur = reponse.body.getReader();
  const decodeur = new TextDecoder();
  let tampon = "";

  try {
    for (;;) {
      const { done, value } = await lecteur.read();
      if (done) return { raison: "FIN" };
      tampon += decodeur.decode(value, { stream: true });
      const { evenements, reste } = analyserTrames(tampon);
      tampon = reste;
      for (const e of evenements) {
        try {
          onEvenement?.(e);
        } catch {
          /* un abonné défaillant ne casse jamais la pompe */
        }
      }
    }
  } catch {
    return { raison: signal?.aborted ? "ABANDON" : "RUPTURE" };
  } finally {
    try {
      lecteur.cancel();
    } catch {
      /* déjà close */
    }
  }
}

export default { analyserTrames, ouvrirFlux };
